import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { fDate } from '../util/format-time';

import Iconify from '../../theme/iconify/iconify';
import SvgColor from '../../theme/svg-color/svg-color';

function CorpusCard({ corpus }) {
    const { i18n, t } = useTranslation();
    const navigate = useNavigate();

    const renderAvatar = (
        <Avatar
            id='mISLec_avatar_icon'
            alt='Corpus icon'
            src='/assets/icons/file-storage.png'
            sx={{
                zIndex: 9,
                width: 32,
                height: 32,
                position: 'absolute',
                left: 27,
                bottom: -18,
                scale: '75% !important'
            }}
        />
    );

    const renderTitle = (
        <Link
            color='inherit'
            variant='subtitle2'
            underline='hover'
            onClick={() => navigate(`/corpus/${corpus._id}`)}
            sx={{
                height: 44,
                overflow: 'hidden',
                WebkitLineClamp: 2,
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical'
            }}
        >
            {corpus.title}
        </Link>
    );

    const renderInfo = (
        <Stack
            direction='row'
            flexWrap='wrap'
            spacing={1.5}
            justifyContent='flex-end'
            sx={{
                mt: 2,
                color: 'text.disabled',
            }}
        >
            <Tooltip title={t('corpuses.card.tooltips.document')} arrow>
                <Stack
                    direction='row'
                    sx={{
                        p: 0.5,
                        borderRadius: '8px',
                        '&:hover': {
                            backgroundColor: 'rgba(145, 158, 171, 0.08)',
                        },
                    }}
                >
                    <Iconify width={16} icon='ion:document-outline' sx={{ mr: 0.5 }} />
                    <Typography variant='caption'>{corpus.documents.length}</Typography>
                </Stack>
            </Tooltip>
        </Stack>
    );

    const renderDate = (
        <Typography
            variant='caption'
            component='div'
            sx={{
                mb: 2,
                color: 'text.disabled'
            }}
        >
            {fDate(corpus.timestamp, i18n.language)}
        </Typography>
    );

    const renderShape = (
        <SvgColor
            color='paper'
            src='/assets/icons/shape-avatar.svg'
            sx={{
                width: 80,
                height: 36,
                zIndex: 9,
                left: 3,
                bottom: -16,
                position: 'absolute',
                color: 'background.paper',
                borderRadius: '16px'
            }}
        />
    );

    return (
        <Grid item xs={12} sm={6} md={3} sx={{ mb: 1 }}>
            <Paper elevation={2}>
                <Box sx={{ position: 'relative' }}>
                    {renderShape}
                    {renderAvatar}
                </Box>

                <Box
                    sx={{
                        p: (theme) => theme.spacing(4, 3, 2, 3),
                        backgroundColor: 'white',
                        borderRadius: '16px'
                    }}
                >
                    {renderDate}
                    {renderTitle}
                    {renderInfo}
                </Box>
            </Paper>
        </Grid>
    );
}

CorpusCard.propTypes = {
    corpus: PropTypes.object.isRequired
};

export default CorpusCard;